'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Swiper as SwiperInstance } from 'swiper';
import { ApplyCtaLink, useCtaResolver } from '../../components/ApplyCta';
import { CmsImage, getResponsiveUrl } from '../../components/CmsImage';
import { FeCarouselV2 } from '../../components/FeCarousel';
import { FeLink } from '../../components/FeLink';
import { MaterialIcon } from '../../components/MaterialIcon';
import {
  ComponentTypeEnum,
  ELEMENT_IMPRESSION_VIEW_DWELL_MS,
  getProductContextParameter,
  getRewardCampaignId,
  getRewardCampaignName,
  sendProductAnalytics,
  useElementInView,
  UserActionEnum,
} from '../../utils/analytics';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { isSameSiteLink } from '../../utils/getLinkType';
import { sessionAttributionTracker } from '../../utils/sessionAttributionTracking/sessionAttributionTracker';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { TRANSLATIONS } from './i18n';
import { ImageCarouselProps, ImageCarouselSlide } from './types';

/**
 * Layout follows the Figma component "image carousel" (28490:31082 and its
 * 768 / 360 siblings): optional heading, then the banner, then the dots.
 *
 * The banner takes the full width of whatever column it is placed in and holds an
 * 8:1 aspect ratio, rather than hardcoding the spec's per-breakpoint pixel boxes:
 *
 *   360px  -> 328 x 41   (328/41  = 8.00)
 *   768px  -> 720 x 90   (720/90  = 8.00)
 *   1280px -> 1130 x 141 (1130/141 = 8.01)
 *
 * Those three widths are exactly what PageContentContainer already yields at the
 * spec viewports (`px-spacing/padding/section-x` outside,
 * `max-w-[--breakpoints-content-narrow-3-lg]` = 1130 inside), so `w-full`
 * reproduces them — and, unlike fixed widths, it keeps working BETWEEN the
 * breakpoints. With `md:w-[720px]` the banner stayed 720px across the whole
 * 768-1279 range while the column grew to 1130, leaving up to ~410px of centred
 * empty space on a normal laptop window. Every other consumer of this token
 * (StickyNavigationBar, ProductDetailPageMode, AnchorStickyFooterComponent,
 * PortalPlace) likewise takes its width from the container.
 *
 * The aspect ratio is load-bearing, not decorative: `w-full` on its own would
 * leave the fixed heights in place and stretch the artwork at intermediate widths.
 * It also reserves the box before the image loads, so the image load itself shifts
 * nothing. It does NOT on its own make the first paint stable — the box is only as
 * correct as the slide width it sits in, which is why `beforeInitSlideClassName` below
 * is equally load-bearing.
 *
 * Note the two breakpoint vocabularies below are OFF BY ONE — CmsImage `sizes`
 * (utils/breakpoints) uses xs=360, sm=768, md=1280, lg=1440, whereas Tailwind's
 * modulab preset uses sm=360, md=768, lg=1280, xl=1440.
 *
 * KEEP THE WIDTH CHAIN RESOLVABLE. FeCarouselV2's root is a plain block div and the
 * Swiper element inside asks for `w-full`, so every ancestor up to a definite width
 * must be width-resolvable too. Drop this block into a content-sized ancestor (an
 * inline-flex or a flex item with no basis) and the percentage and the content size
 * resolve circularly: Chrome falls back to ~2^24px, Swiper measures 16777216px-wide
 * slides, the wrapper transform never really moves, no `transitionend` fires, and
 * autoplay — which resumes on that event — stalls permanently after one slide,
 * while still looking like a working carousel in a screenshot.
 */
export const IMAGE_BOX_CLASSNAME = 'aspect-[8/1] h-auto w-full';

/**
 * The slot is fluid below 1280 (it is the column width, see IMAGE_BOX_CLASSNAME), so a
 * per-breakpoint pixel ladder mis-describes it everywhere except at the four spec
 * viewports, and the browser then picks a bucket that has to be stretched: at a 1279
 * DPR-1 window `720px` claims the 768 asset for a ~1130 slot (1.47x), at 600 it claims
 * `328px` and gets 360 for a ~568 slot (1.58x). Visible as soft edges on artwork that
 * carries text. `100vw` over-states only by the 32-48px gutters — never enough to cross
 * up a bucket. Above 1280 the column is pinned at 1130 (`--breakpoints-content-narrow-3-lg`),
 * so those two stay exact.
 *
 * Consequence to know about: next/image drops the 180/360 candidates from the srcset as
 * soon as `sizes` contains any `vw` value (it keeps only widths >= `deviceSizes[0]`, and
 * `next.config.mjs` starts at 768), so a DPR-1 phone now fetches the 768 variant instead
 * of 360. Cheap on an 8:1 banner, and DPR>=2 phones needed >=656 anyway.
 */
const IMAGE_SIZES = { _: '100vw', md: 1130, lg: 1130 } as const;

/**
 * Every box holds the same ~8:1 ratio as the aspect-ratio above, so filling it is
 * effectively a no-op resize. Kept explicit because the spec fixes the box:
 * switching to `object-contain` (letterbox strips) or `object-cover` (crop) is
 * this one class.
 */
export const IMAGE_FIT_CLASSNAME = 'object-fill';

// 767.98, not 767: a fractional-width viewport must never land between this query
// and the Tailwind `md:` (min-width: 768px) overrides on the <img> (CMS-1971).
const MOBILE_MEDIA_QUERY = '(max-width: 767.98px)';

// The mobile spec asset is 3:1 (1200 × 400) with `object-fit: cover`; from `md` up
// this must mirror IMAGE_BOX_CLASSNAME / IMAGE_FIT_CLASSNAME exactly (CMS-1971).
// Duplicated as literals, not composed from the constants: Tailwind only scans complete class strings.
export const ART_DIRECTED_BOX_CLASSNAME =
  'aspect-[3/1] h-auto w-full object-cover md:aspect-[8/1] md:object-fill';

// Bucket ladder covering a sub-768px, full-container slot at DPR 1–3. getResponsiveUrl
// caps at the asset's largest real variant, so duplicate candidates collapse below.
const MOBILE_SRCSET_WIDTHS = [360, 768, 1280];

const buildMobileSrcSet = (url: string): string => {
  const variants = new Map<string, number>();
  for (const width of MOBILE_SRCSET_WIDTHS) {
    const variant = getResponsiveUrl(url, width);
    if (!variants.has(variant)) variants.set(variant, width);
  }
  if (variants.size === 1) return [...variants.keys()][0];
  return [...variants]
    .map(([variant, width]) => `${variant} ${width}w`)
    .join(', ');
};

/** Used when content-ops leaves the interval unset. */
const DEFAULT_INTERVAL_SECONDS = 5;

/**
 * Slide gap = the `spacing/gap-x` token (16 / 20 / 24 / 24px). Passed as numbers
 * because Swiper needs the gap in its own slide-pitch maths, not just as CSS.
 */
const SLIDE_GAP_BASE = 16;
const SLIDE_GAP_BREAKPOINTS: [number, number][] = [
  [20, 768],
  [24, 1280],
];

/**
 * The heading is 20/24 at 360, 24/31 at 768 and 28/36 from 1280 — which is
 * `display-1` below 1280 and `display-2` at and above it. No single token spans
 * all three, hence the one breakpoint override.
 */
const TITLE_CLASSNAME =
  'mhg-alpha-typography-display-1 lg:mhg-alpha-typography-display-2 text-text/primary';

/**
 * Prev/next per Figma: a white circle with `shadow-md` and a grey arrow, whose
 * CENTRE sits on the image's left/right edge (so it half-overlaps the banner) and
 * is vertically centred on the image. 30x30 with a 24px icon from 768 up, 20x20
 * with a 16px icon at 360.
 *
 * FeCarouselV2 parks its arrows outside the carousel at `-left-6` / `-right-6`;
 * the overrides below pull them onto the edge. Vertical centring is left to the
 * component's own `top-1/2 -translate-y-1/2`, which lands on the image because
 * the dots are rendered here rather than through Swiper's pagination — so the
 * carousel root is exactly as tall as the image.
 */
const CONTROL_CLASSNAME = [
  'flex items-center justify-center rounded-full bg-fill/card text-buttons/neutral shadow-md',
  'size-[20px] md:size-[30px]',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fill/primary',
].join(' ');

/** Dots: `shapes/icon-size-sm` (16 at 360, 18 from 768), gapped by `spacing/padding/soft-4px`. */
const DOT_ICON_SM = 16;
const DOT_ICON_MD = 18;

const isRenderableSlide = (slide: ImageCarouselSlide): boolean =>
  !!slide.url?.trim();

/**
 * `block w-full` keeps the width chain resolvable (see the header comment) — an
 * inline anchor would content-size the aspect box and re-create the 2^24px failure.
 */
const SLIDE_LINK_CLASSNAME = 'block w-full';

/**
 * `rel` is derived from the resolved URL, never authored by content-ops (CMS-1968).
 * Strict host match on purpose: `sponsored` carries SEO weight, so a partner
 * host that merely contains ours must still count as external.
 */
const slideLinkRel = (
  href: string,
  target: '_self' | '_blank',
  host: string,
): string | undefined => {
  const parts = [
    ...(target === '_blank' ? ['noopener'] : []),
    ...(isSameSiteLink(href, host) ? [] : ['sponsored']),
  ];
  return parts.length > 0 ? parts.join(' ') : undefined;
};

/**
 * GA4 component_title prefix — the A&A spec fixes it as the literal
 * "Banner Carousel", regardless of the authored moduleTitle (CMS-1976).
 */
const GA_TITLE_PREFIX = 'Banner Carousel';

/**
 * The slide's alt text is the only per-slide text content-ops author, so it
 * doubles as the spec's `image_title`; empty when none is authored.
 */
const slideImageTitle = (slide: ImageCarouselSlide): string =>
  slide.altText?.trim() || '';

/**
 * `reward_campaign_*` are omitted entirely — not sent blank — when the slide
 * has no product or the product has no offers (A&A spec, CMS-1976).
 */
const slideRewardCampaignFields = (
  slide: ImageCarouselSlide,
): Record<string, string> => {
  const offers = [
    ...(slide.product?.exclusive_offers ?? []),
    ...(slide.product?.bundle_exclusive_offers ?? []),
  ];
  const name = getRewardCampaignName(offers);
  const id = getRewardCampaignId(offers);
  return {
    ...(name ? { reward_campaign_name: name } : {}),
    ...(id ? { reward_campaign_id: id } : {}),
  };
};

const slideImpressionPayload = (
  slide: ImageCarouselSlide,
  index: number,
  componentIndex: string | undefined,
  action: UserActionEnum.LOAD | UserActionEnum.VIEW,
) => ({
  event: 'element_impression',
  action,
  component_type: ComponentTypeEnum.IMAGE_CAROUSEL,
  component_title: `${GA_TITLE_PREFIX}/Image_${slideImageTitle(slide)}`,
  component_index: componentIndex ?? '',
  index: String(index),
});

export const ImageCarousel = ({
  visible,
  moduleTitle,
  autoscrollInterval,
  slides,
  slug,
  componentIndex,
  pushDataLayer,
}: ImageCarouselProps): JSX.Element | null => {
  const { brand, lang, host } = useCmsClientContext();
  const t = getClientTranslations(TRANSLATIONS, { brand, lang });
  const resolveCta = useCtaResolver();

  // Product slides carry the same resolver inputs as every ApplyCtaLink
  // consumer (cta-link-rules.md P0.1–P3.1). Resolved here as well because a
  // slide whose CTA yields no URL must fall through to a plain image, not
  // ApplyCtaLink's null-on-no-href. A slide with only an override is NOT a
  // product placement: the authored URL renders verbatim, without the
  // resolver's TI criteria / catalogue-version rewriting (review, CMS-1968).
  const slideCtaInputs = (slide: ImageCarouselSlide) => ({
    product: slide.product,
    ctaLinkOverride: slide.overrideCtaUrl?.trim() || undefined,
  });
  const resolveSlideHref = (slide: ImageCarouselSlide): string | undefined =>
    slide.product
      ? resolveCta(slideCtaInputs(slide)) || undefined
      : slide.overrideCtaUrl?.trim() || undefined;
  const swiperRef = useRef<SwiperInstance | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Auto-scroll must not start for visitors who ask for reduced motion. The
  // check has to happen after mount (matchMedia in render would break SSR
  // hydration), and the decision is applied by remounting via `key` rather than
  // by toggling autoplay on a live instance. Default is `true` so the common
  // case never remounts.
  const [motionAllowed, setMotionAllowed] = useState(true);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setMotionAllowed(!query.matches);

    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, []);

  const renderableSlides = useMemo(
    () => slides.filter(isRenderableSlide),
    [slides],
  );

  // Served (`load`, #2541) fires once per slide per pageview, viewport-independent;
  // viewed (`view`, #2564) re-fires on every qualifying display, loops included.
  const sectionRef = useRef<HTMLElement | null>(null);
  const inView = useElementInView(sectionRef);
  const servedImpressionIds = useRef(new Set<string>());

  useEffect(() => {
    if (!pushDataLayer) return;
    const slide = renderableSlides[activeIndex];
    if (!slide || servedImpressionIds.current.has(slide.id)) return;
    servedImpressionIds.current.add(slide.id);
    pushDataLayer(
      slideImpressionPayload(
        slide,
        activeIndex,
        componentIndex,
        UserActionEnum.LOAD,
      ),
    );
  }, [activeIndex, renderableSlides, componentIndex, pushDataLayer]);

  useEffect(() => {
    if (!pushDataLayer || !inView) return;
    const slide = renderableSlides[activeIndex];
    if (!slide) return;
    // The cleanup IS the dwell rule: rotating away or leaving the viewport
    // before the timer fires cancels the pending viewed impression.
    const timer = setTimeout(
      () =>
        pushDataLayer(
          slideImpressionPayload(
            slide,
            activeIndex,
            componentIndex,
            UserActionEnum.VIEW,
          ),
        ),
      ELEMENT_IMPRESSION_VIEW_DWELL_MS,
    );
    return () => clearTimeout(timer);
  }, [inView, activeIndex, renderableSlides, componentIndex, pushDataLayer]);

  if (visible === false || renderableSlides.length === 0) {
    return null;
  }

  // A single image is a static image: no auto-scroll, no arrows, no dots
  // (AC "Single image disables carousel behavior").
  const isCarousel = renderableSlides.length > 1;
  const autoplayEnabled = isCarousel && motionAllowed;
  const title = moduleTitle?.trim();

  // Auto-scroll pauses while anything inside the carousel holds keyboard focus.
  // stop()/start() rather than Swiper's own pause()/resume(): pause() parks the
  // timer until a `transitionend` that never arrives when no slide transition is
  // in flight, which would stall auto-scroll for good.
  const handleFocusEnter = () => {
    if (autoplayEnabled) swiperRef.current?.autoplay?.stop();
  };
  const handleFocusLeave = (event: React.FocusEvent<HTMLDivElement>) => {
    // Ignore focus moving between elements inside the carousel.
    if (event.currentTarget.contains(event.relatedTarget as Node | null))
      return;
    if (autoplayEnabled) swiperRef.current?.autoplay?.start();
  };

  // Reports the pre-click slide. Must be capture-phase: by the bubbling click,
  // Swiper's own listener has navigated and React has already flushed the
  // slideChange state update, so `activeIndex` would name the target slide.
  const trackControlClick = (direction: 'Next' | 'Previous') => {
    if (!pushDataLayer) return;
    const slide = renderableSlides[activeIndex];
    if (!slide) return;
    pushDataLayer({
      event: 'navigate',
      component_type: ComponentTypeEnum.BUTTON,
      component_title: `${GA_TITLE_PREFIX}/${direction}_${slideImageTitle(slide)}`,
      component_index: componentIndex ?? '',
      action: UserActionEnum.CLICK,
      index: String(activeIndex),
      ...slideRewardCampaignFields(slide),
    });
  };

  // Slides default to APPLY_CLICK, like the rendered data-click-type (CMS-1968).
  const trackSlideClick = (slide: ImageCarouselSlide, index: number) => {
    if (!pushDataLayer) return;
    const componentTitle = `${GA_TITLE_PREFIX}/Image_${slideImageTitle(slide)}`;
    const isApplyClick = (slide.clickType ?? 'APPLY_CLICK') === 'APPLY_CLICK';

    if (!isApplyClick) {
      pushDataLayer({
        event: 'navigate',
        component_type: ComponentTypeEnum.IMAGE_CAROUSEL,
        component_title: componentTitle,
        component_index: componentIndex ?? '',
        action: UserActionEnum.CLICK,
        index: String(index),
        ...slideRewardCampaignFields(slide),
      });
      return;
    }

    sessionAttributionTracker.recordApplyClick();
    if (slide.product) {
      sendProductAnalytics({
        event: 'product_click_apply',
        action: UserActionEnum.CLICK,
        componentType: ComponentTypeEnum.IMAGE_CAROUSEL,
        componentTitle,
        itemListName: slug,
        product: slide.product,
        index: String(index),
        pushDataLayer,
        extraData: { component_index: componentIndex ?? '' },
      });
    } else {
      // No linked product → nothing to fill an `items` entry with, and no
      // top-level `index` — per spec #2544 it lives inside `items`.
      pushDataLayer({
        ...(slug ? { item_list_name: slug } : {}),
        event: 'product_click_apply',
        action: UserActionEnum.CLICK,
        component_type: ComponentTypeEnum.IMAGE_CAROUSEL,
        component_title: componentTitle,
        component_index: componentIndex ?? '',
        product_context_parameter: getProductContextParameter(),
      });
    }
  };

  return (
    <section
      ref={sectionRef}
      className="w-full"
      data-component="image-carousel"
    >
      <div
        className="flex w-full flex-col gap-spacing/gap/x-sm"
        onFocus={handleFocusEnter}
        onBlur={handleFocusLeave}
      >
        {/* Omitted entirely when content-ops leaves the title empty. */}
        {title && <h2 className={TITLE_CLASSNAME}>{title}</h2>}

        <FeCarouselV2
          // Remounts only when the reduced-motion answer flips (see above).
          key={autoplayEnabled ? 'autoplay' : 'static'}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            swiper.on('slideChange', () => setActiveIndex(swiper.activeIndex));
          }}
          slidesPerView={1}
          // Pin the slide width for the first paint. Swiper writes real slide widths
          // only in JS, and FeCarouselV2 puts `w-auto` on every slide, which beats
          // `.swiper-slide{width:100%}` (equal specificity, and swiper-bundle.css is
          // imported above `@tailwind utilities`). So in the SSR HTML the slide is
          // content-sized to the image's intrinsic width={1130}, and the aspect ratio
          // then makes the box 141.25px tall at every viewport — up to a 100px vertical
          // snap on a phone once Swiper initialises, plus a first paint that is ~3.4x
          // too wide inside `overflow:hidden`. `w-full` reproduces exactly what Swiper
          // computes for slidesPerView={1}; `!` because `w-auto` sits on the same
          // element at equal specificity. The class is dropped after init, so Swiper's
          // inline width still wins. Same mechanism as ProductCardGrid/RewardCardGrid.
          // Storybook cannot catch this: it is client-rendered and Swiper initialises in
          // a layout effect, i.e. before paint — it only shows on a real SSR page.
          beforeInitSlideClassName="!w-full"
          // Arrows wrap instead of dead-ending, matching what autoplay already
          // does at the last slide. Without it prev/next go disabled at the ends
          // while auto-scroll keeps cycling, and that disabled state is neither
          // visible (the button sets its own colour, beating the inherited one)
          // nor announced (Swiper's A11y module isn't enabled here) — a
          // focusable control that silently does nothing.
          rewind
          spaceBetween={SLIDE_GAP_BASE}
          spaceBetweenBreakpoints={SLIDE_GAP_BREAKPOINTS}
          autoplay={
            autoplayEnabled && {
              delay:
                (autoscrollInterval && autoscrollInterval > 0
                  ? autoscrollInterval
                  : DEFAULT_INTERVAL_SECONDS) * 1000,
              // Pause while the pointer rests on the carousel, and resume when it
              // leaves. Swiper also pauses on drag and restarts the timer from the
              // slide the visitor landed on, which is what the manual-control ACs ask for.
              pauseOnMouseEnter: true,
              disableOnInteraction: false,
            }
          }
          navigation={
            isCarousel
              ? {
                  showOnDesktop: true,
                  showOnMobile: true,
                  prevElClassName: '!left-0 -translate-x-1/2',
                  nextElClassName: '!right-0 translate-x-1/2',
                  // Real buttons so the controls are keyboard-operable with a
                  // visible focus ring. They render inside the element Swiper binds
                  // its click handler to, so Enter/Space reaches Swiper by bubbling.
                  prevElement: (
                    <button
                      type="button"
                      aria-label={t.image_carousel_previous_slide}
                      className={CONTROL_CLASSNAME}
                      onClickCapture={() => trackControlClick('Previous')}
                    >
                      <MaterialIcon
                        icon="arrow_back"
                        px={16}
                        className="block md:hidden"
                      />
                      <MaterialIcon
                        icon="arrow_back"
                        px={24}
                        className="hidden md:block"
                      />
                    </button>
                  ),
                  nextElement: (
                    <button
                      type="button"
                      aria-label={t.image_carousel_next_slide}
                      className={CONTROL_CLASSNAME}
                      onClickCapture={() => trackControlClick('Next')}
                    >
                      <MaterialIcon
                        icon="arrow_forward"
                        px={16}
                        className="block md:hidden"
                      />
                      <MaterialIcon
                        icon="arrow_forward"
                        px={24}
                        className="hidden md:block"
                      />
                    </button>
                  ),
                }
              : undefined
          }
        >
          {renderableSlides.map((slide, index) => {
            const mobileUrl = slide.mobileUrl?.trim();
            const img = (
              <CmsImage
                key={slide.id}
                src={slide.url ?? ''}
                alt={slide.altText ?? ''}
                width={1130}
                height={141}
                sizes={IMAGE_SIZES}
                // The first slide loads eagerly so it is ready without waiting on the
                // lazy observer, but deliberately WITHOUT next/image `priority`:
                // content-ops can place this module anywhere, and below the fold a
                // preload + fetchpriority=high steals bandwidth from the real LCP
                // element — regressing the very metric the NFR wants protected. No
                // other cms-block uses `priority`; the ones that do are always-visible
                // surfaces.
                loading={index === 0 ? 'eager' : 'lazy'}
                className={`${
                  mobileUrl
                    ? ART_DIRECTED_BOX_CLASSNAME
                    : `${IMAGE_BOX_CLASSNAME} ${IMAGE_FIT_CLASSNAME}`
                } rounded-radius/card-inner`}
              />
            );

            // Same width-chain reason as SLIDE_LINK_CLASSNAME — <picture> is inline by default.
            // width/height on <source> give the browser the 3:1 mobile intrinsic ratio up front.
            const image = mobileUrl ? (
              <picture key={slide.id} className="block w-full">
                <source
                  media={MOBILE_MEDIA_QUERY}
                  srcSet={buildMobileSrcSet(mobileUrl)}
                  sizes="100vw"
                  width={1200}
                  height={400}
                />
                {img}
              </picture>
            ) : (
              img
            );

            const href = resolveSlideHref(slide);
            if (!href) {
              // v1 behavior: a slide with no resolved destination stays a
              // plain, non-clickable image.
              return image;
            }

            const target = slide.linkTarget === 'SAME_TAB' ? '_self' : '_blank';
            const rel = slideLinkRel(href, target, host);
            const clickType = (slide.clickType ?? 'APPLY_CLICK').toLowerCase();

            // Product → ApplyCtaLink (vertical transforms, UTM routing,
            // apply-v3 modal link); override-only → the authored URL as a
            // plain link.
            return slide.product ? (
              <ApplyCtaLink
                key={slide.id}
                {...slideCtaInputs(slide)}
                target={target}
                rel={rel}
                data-click-type={clickType}
                className={SLIDE_LINK_CLASSNAME}
                onClick={() => trackSlideClick(slide, index)}
              >
                {image}
              </ApplyCtaLink>
            ) : (
              <FeLink
                key={slide.id}
                href={href}
                target={target}
                rel={rel}
                data-click-type={clickType}
                className={SLIDE_LINK_CLASSNAME}
                onClick={() => trackSlideClick(slide, index)}
              >
                {image}
              </FeLink>
            );
          })}
        </FeCarouselV2>

        {/* Dots are rendered here rather than via Swiper's pagination so they can
            use the design's radio-button icons (Swiper's renderBullet only takes an
            HTML string, and raw material-symbols markup is not allowed in this
            codebase). Decorative: they mirror state the slides already convey. */}
        {isCarousel && (
          <div
            aria-hidden="true"
            className="flex justify-center gap-spacing/padding/soft-4px text-text/secondary-lighter"
          >
            {renderableSlides.map((slide, index) => {
              const icon =
                index === activeIndex
                  ? 'radio_button_checked'
                  : 'radio_button_unchecked';
              return (
                <span key={slide.id} className="block">
                  <MaterialIcon
                    icon={icon}
                    px={DOT_ICON_SM}
                    className="block md:hidden"
                  />
                  <MaterialIcon
                    icon={icon}
                    px={DOT_ICON_MD}
                    className="hidden md:block"
                  />
                </span>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
